import { ReactElement, useCallback, useEffect, useMemo } from 'react';

import { ROUTES } from '@/constants/routes';
import { useMarketStore } from '@/features/markets/stores/useMarketStore';
import { castMarketToStoreMarket } from '@/features/markets/types/market';
import { useNavigate } from '@/hooks/useNavigate';
import { callbackUrlFromCurrentLocation } from '@/utils/navigation';

import { useCheckCurrentUser } from '../../api/useCurrentUser';
import { useRefreshToken } from '../../api/useRefreshToken';
import { useAuthStore } from '../../stores/useAuthStore';
import { isAuthTokenExpired } from '../../utils/auth-data';
import { castCurrentUserToAuthUser } from '../../utils/auth-user';

interface RequireAuthProps {
    children: ReactElement | null;
}

export const RequireAuth = ({ children }: RequireAuthProps) => {
    const { navigate } = useNavigate();
    const { authUser, authData, isAuthenticated, setAuthUser, setAuthData, resetAuthState } = useAuthStore();
    const { market, setMarket } = useMarketStore();

    const { refreshToken } = useRefreshToken();
    const { checkCurrentUser } = useCheckCurrentUser();

    const isAuthChecked = useMemo(
        () => isAuthenticated && !!market && !!authUser && !!authData && !isAuthTokenExpired(authData),
        [isAuthenticated, market, authUser, authData],
    );

    //

    const refreshUserData = useCallback(() => {
        if (!authData?.remember) {
            resetAuthState();
            return;
        }

        refreshToken(authData!.refreshToken, {
            onSuccess: (response) => {
                setAuthData({
                    expiredTime: new Date().getTime() + response.expires_in * 1000,
                    accessToken: response.access_token,
                    refreshToken: response.refresh_token,
                    remember: true,
                });
            },
            onError: () => {
                resetAuthState();
            },
        });
    }, [authData, refreshToken, setAuthData, resetAuthState]);

    const rescueCurrentUser = useCallback(() => {
        checkCurrentUser({
            onSuccess: (currentUser) => {
                setAuthUser(castCurrentUserToAuthUser(currentUser));
                setMarket(castMarketToStoreMarket(currentUser.usersMarkets[0].market));
            },
        });
    }, [checkCurrentUser, setAuthUser, setMarket]);

    //

    // NOTE: Refetch auth when token is expired
    useEffect(() => {
        if (!isAuthTokenExpired(authData)) return;

        refreshUserData();
        // NOTE: Only check once, when load page first time
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // NOTE: Refetch market when is authenticated w/o market
    useEffect(() => {
        const notMarketSelected = isAuthenticated && !market;
        if (!notMarketSelected) return;

        rescueCurrentUser();
    }, [isAuthenticated, market, rescueCurrentUser]);

    // NOTE: Redirect to login when there is no authentication
    useEffect(() => {
        if (isAuthenticated) return;

        const callbackUrl = callbackUrlFromCurrentLocation();
        navigate({
            pathname: ROUTES.login.route,
            search: callbackUrl ? new URLSearchParams({ callbackUrl }).toString() : undefined,
        });
        // NOTE: Avoid "navigate" in depends-array to avoid url-updates rerenders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, market]);

    return isAuthChecked ? children : null;
};
